// ============================================================
// SecaoInicio.js — a primeira tela da coordenação
//
// Antes o painel abria em Relatórios, que é uma ferramenta de consulta:
// quem chegava precisava saber de antemão o que queria olhar. Esta tela
// responde à pergunta que se faz ao abrir o sistema, que é "como está o
// núcleo hoje".
//
// Duas regras que orientam o que entra aqui:
//
// 1. Número que não leva a lugar nenhum não entra. Cada cartão abre a tela
//    onde se age sobre aquilo.
// 2. Nada é calculado aqui. Frequência vem de dadosDoNucleo, que usa o
//    mesmo calculo.js dos relatórios. Dois caminhos para o mesmo número é
//    como a planilha do programa acabou com três totais diferentes.
// ============================================================

import { useState, useEffect } from "react";
import { useDadosDoNucleo } from "../dadosDoNucleo";
import { listarPlanejamento, contarSolicitacoesPendentes } from "../firebase";
import { useNomeNucleo } from "../NucleosContexto";
import ProximasCompeticoes from "../componentes/ProximasCompeticoes";
import SemNucleo from "../componentes/SemNucleo";
import {
  AZUL, AZUL_ESCURO, TEXTO, CINZA_ESC, BRANCO, BORDA, RAIO, ESP,
  TIPO, FUNDO_SUAVE, ATENCAO, RUIM,
} from "../tema";
import {
  Card, Estatistica, FileiraDeEstatisticas, Etiqueta, Vazio, Carregando,
  PanoramaDoNucleo,
} from "../componentes";

function saudacao() {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

function fmtData(iso) {
  if (!iso) return "";
  const p = String(iso).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
}

// Quantos dias faltam, contados só por data. Hora não entra: prazo de
// entrega vence no fim do dia, não no instante em que a tela abriu.
function diasAte(iso, hojeISO) {
  if (!iso || !hojeISO) return null;
  const [a1, m1, d1] = hojeISO.split("-").map(Number);
  const [a2, m2, d2] = iso.split("-").map(Number);
  const um = Date.UTC(a1, m1 - 1, d1);
  const outro = Date.UTC(a2, m2 - 1, d2);
  return Math.round((outro - um) / 86400000);
}

export default function SecaoInicio({ userNucleo, userNome, onIr }) {
  const nomeNucleo = useNomeNucleo();
  const d = useDadosDoNucleo(userNucleo);

  const [pendentes, setPendentes] = useState(null);
  const [plan, setPlan] = useState(null);


  useEffect(() => {
    let ativo = true;
    if (!userNucleo) return undefined;
    (async () => {
      try {
        // Só o número, contado no servidor: a fila do dia mostra "3
        // solicitações esperando", e para isso a tela baixava as 95 do
        // núcleo inteiro.
        const quantas = await contarSolicitacoesPendentes(userNucleo);
        if (ativo) setPendentes(quantas);
      } catch (e) { if (ativo) setPendentes(null); }
      try {
        const r = await listarPlanejamento(userNucleo);
        if (ativo) setPlan(r);
      } catch (e) { if (ativo) setPlan(null); }
    })();
    return () => { ativo = false; };
  }, [userNucleo]);

  const primeiroNome = userNome ? String(userNome).split(" ")[0] : null;
  const dataLonga = new Date().toLocaleDateString("pt-BR", {
    weekday: "long", day: "2-digit", month: "long",
  });
  const hojeBR = dataLonga.charAt(0).toUpperCase() + dataLonga.slice(1);

  const trimestre = plan && (plan.trimestres || []).find((t) => t.id === plan.trimestreAtivoId);
  const planos = (plan && plan.planos) || [];
  const esperandoAnalise = planos.filter((p) => p.status === "enviado").length;
  const entregues = planos.filter((p) => p.status === "enviado" || p.status === "aprovado").length;
  const esperados = ((plan && plan.modalidades) || []).length * 2;
  const diasDoPrazo = trimestre ? diasAte(trimestre.prazoEnvio, d.hojeISO) : null;

  // A fila do dia. Só entra o que espera decisão de alguém: lista de
  // pendência que nunca esvazia deixa de ser lida.
  const pendencias = [];
  if (pendentes) {
    pendencias.push({
      chave: "solic",
      texto: `${pendentes} solicitação(ões) de frequência esperando análise`,
      tom: "atencao", aba: "solicitacoes", acao: "Analisar",
    });
  }
  if (esperandoAnalise) {
    pendencias.push({
      chave: "planos",
      texto: `${esperandoAnalise} plano(s) do trimestre esperando sua aprovação`,
      tom: "info", aba: "plan_aprovacoes", acao: "Ver planos",
    });
  }
  if (trimestre && diasDoPrazo !== null && entregues < esperados) {
    pendencias.push({
      chave: "prazo",
      texto: diasDoPrazo < 0
        ? `Prazo de entrega dos planos venceu em ${fmtData(trimestre.prazoEnvio)}, e faltam ${esperados - entregues}`
        : `Faltam ${esperados - entregues} plano(s) e o prazo é ${fmtData(trimestre.prazoEnvio)}`,
      tom: diasDoPrazo < 0 ? "ruim" : diasDoPrazo <= 3 ? "atencao" : "neutro",
      aba: "plan_visao", acao: "Ver o que falta",
    });
  }
  if (!trimestre && plan) {
    pendencias.push({
      chave: "semtrim",
      texto: "Nenhum trimestre aberto. Sem ele, ninguém tem onde entregar o planejamento.",
      tom: "neutro", aba: "plan_ciclos", acao: "Abrir trimestre",
    });
  }

  const porModalidade = d.presentesHoje.filter((g) => g.modalidade !== "Equipe técnica");
  const equipe = d.equipeHoje || [];
  const totalAtletas = porModalidade.reduce((s, g) => s + g.nomes.length, 0);
  const totalHoje = totalAtletas + equipe.length;

  // Sem núcleo escolhido, a tela diz isso em vez de mostrar os números
  // de outra pessoa. Depois dos hooks, de propósito: sair antes deles
  // quebraria a ordem que o React exige.
  if (!userNucleo) return <SemNucleo oQueFaria="abrir o início deste núcleo" />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ESP.lg }}>
      {/* Faixa de abertura */}
      <div style={{
        backgroundColor: AZUL_ESCURO,
        backgroundImage: `linear-gradient(120deg, ${AZUL_ESCURO} 0%, ${AZUL} 62%)`,
        borderRadius: RAIO.lg, padding: "22px 26px", color: BRANCO,
      }}>
        <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.2 }}>
          {saudacao()}{primeiroNome ? `, ${primeiroNome}` : ""}
        </div>
        <div style={{ fontSize: 13, opacity: 0.88, marginTop: 5 }}>
          {hojeBR}
        </div>
        <div style={{ fontSize: 12.5, opacity: 0.8, marginTop: 2 }}>
          Núcleo {nomeNucleo(userNucleo) || userNucleo || "—"}
        </div>
      </div>

      {/* Números do núcleo — a mesma peça da tela de Relatórios. */}
      <PanoramaDoNucleo nucleo={userNucleo} onIr={onIr} />

      {/* O que espera decisão */}
      <Card titulo="Pendências e notificações">
        {pendencias.length === 0 ? (
          <Vazio icone="✓" titulo="Nada pendente no momento">
            Solicitações de frequência e planos enviados aparecem aqui assim que chegam.
          </Vazio>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: ESP.sm }}>
            {pendencias.map((p) => (
              <div key={p.chave} style={{
                display: "flex", alignItems: "center", gap: ESP.md, flexWrap: "wrap",
                border: `1px solid ${BORDA}`, borderRadius: RAIO.md, padding: "11px 14px",
                background: FUNDO_SUAVE,
              }}>
                <Etiqueta tom={p.tom}>
                  {p.tom === "ruim" ? "vencido" : p.tom === "atencao" ? "atenção" : "aviso"}
                </Etiqueta>
                <span style={{ ...TIPO.corpo, color: TEXTO, flex: 1, minWidth: 180 }}>{p.texto}</span>
                {onIr && (
                  <button onClick={() => onIr(p.aba)} style={{
                    background: "none", border: "none", color: AZUL, fontSize: 12.5,
                    fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}>
                    {p.acao} →
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Quem já registrou hoje */}
      <Card
        titulo="Presença de hoje"
        sub={totalHoje ? `${totalHoje} registro(s) até agora, entre atletas e equipe` : undefined}
      >
        {d.carregando ? (
          <Carregando linhas={3} />
        ) : totalHoje === 0 ? (
          <Vazio icone="📅" titulo="Nenhum registro hoje">
            Os registros aparecem aqui no instante em que a presença é confirmada, pelo
            aplicativo ou pelo portal.
          </Vazio>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: ESP.lg }}>
            {porModalidade.length > 0 && (
              <div>
                <div style={{ ...TIPO.rotulo, color: CINZA_ESC, marginBottom: ESP.sm }}>
                  Atletas · {totalAtletas}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: ESP.md }}>
                  {porModalidade.map((g) => (
                    <div key={g.modalidade}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: ESP.sm }}>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: AZUL_ESCURO }}>{g.modalidade}</span>
                        <span style={{ fontSize: 12, color: CINZA_ESC }}>{g.nomes.length} registro(s)</span>
                      </div>
                      <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 3 }}>
                        {g.nomes.join(", ")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {equipe.length > 0 && (
              <div style={{ borderTop: `1px solid ${BORDA}`, paddingTop: ESP.md }}>
                <div style={{ ...TIPO.rotulo, color: CINZA_ESC, marginBottom: ESP.sm }}>
                  Equipe técnica · {equipe.length}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {equipe.map((e) => (
                    <div key={e.nome} style={{ display: "flex", alignItems: "baseline", gap: ESP.sm, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13.5, color: TEXTO }}>{e.nome}</span>
                      <span style={{ fontSize: 12, color: CINZA_ESC }}>{e.papel}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Trimestre em curso */}
      {trimestre && (
        <Card
          titulo={`Trimestre em curso: ${trimestre.nome}`}
          sub={`${fmtData(trimestre.dataInicio)} a ${fmtData(trimestre.dataFim)}`}
          acao={onIr && (
            <button onClick={() => onIr("plan_visao")} style={{
              background: "none", border: "none", color: AZUL, fontSize: 12.5,
              fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            }}>
              Abrir planejamento →
            </button>
          )}
        >
          <FileiraDeEstatisticas minimo={140}>
            <Estatistica valor={`${entregues} de ${esperados || "—"}`} rotulo="Planos entregues" cor={AZUL} />
            <Estatistica valor={esperandoAnalise} rotulo="Esperando análise" cor={esperandoAnalise ? ATENCAO : CINZA_ESC} />
            <Estatistica
              valor={diasDoPrazo === null ? "—" : diasDoPrazo < 0 ? `${-diasDoPrazo} dia(s)` : `${diasDoPrazo} dia(s)`}
              rotulo={diasDoPrazo !== null && diasDoPrazo < 0 ? "Prazo vencido há" : "Para o prazo de entrega"}
              contexto={fmtData(trimestre.prazoEnvio)}
              cor={diasDoPrazo !== null && diasDoPrazo < 0 ? RUIM : AZUL}
            />
          </FileiraDeEstatisticas>
        </Card>
      )}

      {/* As próximas competições, no fim da página.
          Ficam até o DIA da competição, inclusive: quem abre o portal na
          manhã do evento precisa vê-lo ali. No dia seguinte a competição
          sai sozinha, porque o lugar dela passa a ser o lançamento de
          resultado. */}
      <ProximasCompeticoes onIr={onIr} />
    </div>
  );
}
